import { InputAdornment, MenuItem, TextField } from "@mui/material"
import { CSSProperties, useMemo, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";

/**
 * DefaultFormAutocomplete
 */

type Props<F extends {}> = {
  colorIdName: keyof F,
  colorName: keyof F,
  colorValue: keyof F,
  label?: string,
  required?: boolean,
  readOnly?: boolean,
  placeholder?: string,
  fullWidth?: boolean,
  reactHookForm: UseFormReturn<F>,
  onChangeValue?: (cor: number | null) => void;
};

export const ColorFormInput = <F extends {},>({
  colorIdName,
  colorName,
  colorValue,
  label,
  required,
  fullWidth,
  reactHookForm,
  onChangeValue,
}: Props<F>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [color, setColor] = useState<CSSProperties['color']>();
  const [borderColor, setBorderColor] = useState<CSSProperties['color']>();

  const options = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Vermelho',
        cor: '#ff0000',
        borderColor: '#ffffff',
      },
      {
        id: 2,
        label: 'Amarelo',
        cor: '#ffff00',
        borderColor: '#000000',
      },
      {
        id: 3,
        label: 'Azul',
        cor: '#3030ff',
        borderColor: '#ffffff',
      },
      {
        id: 4,
        label: 'Verde',
        cor: '#00ff4c',
        borderColor: '#000000',
      },
      {
        id: 5,
        label: 'Branco',
        cor: '#ffffff',
        borderColor: '#000000',
      },
      {
        id: 6,
        label: 'Preto',
        cor: '#000000',
        borderColor: '#ffffff',
      },
      {
        id: 7,
        label: 'Rosa',
        cor: '#ff52d4',
        borderColor: '#ffffff',
      },
      {
        id: 8,
        label: 'Laranja',
        cor: '#ff8800',
        borderColor: '#ffffff',
      },
      {
        id: 9,
        label: 'Cinza',
        cor: '#8d8d8d',
        borderColor: '#ffffff',
      },
    ];
  }, [
  ]);

  return (
    <Controller
      name={colorIdName as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let helperText: string | null = null;
        let value: any = '';

        if (options && field.value) {
          const itemValue = options.find(x => x.id === field.value);
          if (itemValue && itemValue.id) {
            value = itemValue.id;
            requestAnimationFrame(() => {
              setColor(itemValue.cor);
              setBorderColor(itemValue.borderColor);
            });
          }
        }

        if (formState.errors[colorIdName as keyof typeof formState.errors]) {
          const fieldError = formState.errors[colorIdName] as any;
          if (fieldError.message && typeof fieldError.message === 'string') {
            helperText = fieldError.message;
          }
        }

        return (
          <TextField
            {...field}
            select={true}
            label={label}
            fullWidth={fullWidth}
            required={required}
            variant="outlined"
            size="small"
            sx={{ minHeight: 40 }}
            value={value}
            error={helperText !== null}
            helperText={helperText}
            onChange={(event) => {
              if (event.target.value) {
                const item = options.find(x => x.id === Number(event.target.value));
                if (item) {
                  if (onChangeValue !== undefined) onChangeValue(item.id);
                  setColor(item.cor);
                  setBorderColor(item.borderColor);
                  field.onChange(item.id);
                  reactHookForm.setValue(colorName as any, item.label as any);
                  reactHookForm.setValue(colorValue as any, item.cor as any);
                } else {
                  field.onChange('');
                  reactHookForm.resetField(colorName as any);
                  reactHookForm.resetField(colorValue as any);
                }
              } else {
                field.onChange('');
                reactHookForm.resetField(colorName as any);
                reactHookForm.resetField(colorValue as any);
              }
            }}
            slotProps={{
              select: {
                open: isOpen,
                onOpen: () => setIsOpen(true),
                onClose: () => setIsOpen(false),
                endAdornment: (
                  <InputAdornment
                    onClick={() => setIsOpen(prev => !prev)}
                    className="cursor-pointer"
                    position="end">
                    <div
                      className="h-[1.85rem] w-[4.0rem] rounded flex flex-col gap-0 justify-center items-center mr-4 border"
                      style={{
                        backgroundColor: color as CSSProperties['color'] ?? 'transparent',
                        borderColor: borderColor as CSSProperties['color'] ?? 'transparent',
                      }}>
                      <div className="flex flex-col gap-0 justify-center items-center">
                      </div>
                    </div>
                  </InputAdornment>
                ),
              }
            }}>
            {options.map(x => <MenuItem key={x.id} value={x.id}>{x.label}</MenuItem>)}
          </TextField>
        );
      }}
    />
  );
}